import * as React from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface MoneyInputProps extends Omit<React.ComponentProps<"input">, "type" | "value" | "onChange" | "onBlur"> {
  value?: string | number | null;
  onChange?: (value: string) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  currencyPrefix?: string;
  maxIntegerDigits?: number;
  maxDecimalDigits?: number;
}

const formatIntegerWithCommas = (digits: string): string => {
  const safe = digits === "" ? "0" : digits;
  return safe.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const normalizeExternalValue = (
  value: string | number | null | undefined,
  maxIntegerDigits: number,
  maxDecimalDigits: number
): string => {
  if (value === null || value === undefined || value === "") return "";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "";
  const fixed = numeric.toFixed(maxDecimalDigits);
  const [integerPartRaw = "0", decimalPartRaw = ""] = fixed.split(".");
  const integerPart = integerPartRaw.slice(0, maxIntegerDigits).replace(/^0+(?=\d)/, "");
  const decimalPart = decimalPartRaw.slice(0, maxDecimalDigits);
  return decimalPart ? `${integerPart || "0"}.${decimalPart}` : integerPart || "0";
};

const normalizeTypedValue = (
  input: string,
  maxIntegerDigits: number,
  maxDecimalDigits: number
): string => {
  const cleaned = input.replace(/[^\d.,]/g, "");
  if (!cleaned) return "";

  const lastDot = cleaned.lastIndexOf(".");
  const lastComma = cleaned.lastIndexOf(",");
  const separatorIndex = Math.max(lastDot, lastComma);
  const hasSeparator = separatorIndex >= 0;
  const hasTrailingSeparator = hasSeparator && separatorIndex === cleaned.length - 1;

  let integerPart = "";
  let decimalPart = "";

  if (!hasSeparator) {
    integerPart = cleaned.replace(/\D/g, "");
  } else {
    integerPart = cleaned.slice(0, separatorIndex).replace(/\D/g, "");
    decimalPart = cleaned
      .slice(separatorIndex + 1)
      .replace(/\D/g, "")
      .slice(0, maxDecimalDigits);
  }

  integerPart = integerPart.replace(/^0+(?=\d)/, "").slice(0, maxIntegerDigits);

  if (hasSeparator) {
    if (hasTrailingSeparator && decimalPart.length === 0) {
      return `${integerPart || "0"}.`;
    }
    if (decimalPart.length > 0) {
      return `${integerPart || "0"}.${decimalPart}`;
    }
  }

  return integerPart || "";
};

const zeroFormatted = (maxDecimalDigits: number): string => Number(0).toFixed(maxDecimalDigits);

const formatForBlur = (rawValue: string, maxDecimalDigits: number): string => {
  if (!rawValue) return zeroFormatted(maxDecimalDigits);

  const normalized = rawValue.endsWith(".") ? rawValue.slice(0, -1) : rawValue;
  const numeric = Number(normalized);

  if (!Number.isFinite(numeric)) {
    return zeroFormatted(maxDecimalDigits);
  }

  const fixed = numeric.toFixed(maxDecimalDigits);
  const [integerPart, decimalPart = ""] = fixed.split(".");
  return decimalPart.length > 0
    ? `${formatIntegerWithCommas(integerPart)}.${decimalPart}`
    : formatIntegerWithCommas(integerPart);
};

const toStorageValue = (rawValue: string): string => {
  if (!rawValue) return "";
  return rawValue.endsWith(".") ? rawValue.slice(0, -1) : rawValue;
};

const MoneyInput = React.forwardRef<HTMLInputElement, MoneyInputProps>(
  (
    {
      value,
      onChange,
      onBlur,
      currencyPrefix = "US$",
      maxIntegerDigits = 9,
      maxDecimalDigits = 2,
      className,
      ...props
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const [rawValue, setRawValue] = React.useState<string>(() =>
      normalizeExternalValue(value, maxIntegerDigits, maxDecimalDigits)
    );

    React.useEffect(() => {
      if (!isFocused) {
        setRawValue(normalizeExternalValue(value, maxIntegerDigits, maxDecimalDigits));
      }
    }, [value, isFocused, maxIntegerDigits, maxDecimalDigits]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const normalized = normalizeTypedValue(e.target.value, maxIntegerDigits, maxDecimalDigits);
      setRawValue(normalized);
      onChange?.(toStorageValue(normalized));
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);

      const storage = toStorageValue(rawValue);
      const numeric = Number(storage || "0");
      if (storage !== "" && Number.isFinite(numeric) && numeric === 0) {
        setRawValue("");
      }

      props.onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);

      const storage = toStorageValue(rawValue);
      if (!storage) {
        setRawValue("0");
        onChange?.("0");
      } else {
        const numeric = Number(storage);
        const fixed = Number.isFinite(numeric) ? numeric.toFixed(maxDecimalDigits) : zeroFormatted(maxDecimalDigits);
        const normalized = normalizeExternalValue(fixed, maxIntegerDigits, maxDecimalDigits);
        setRawValue(normalized);
        onChange?.(fixed);
      }

      onBlur?.(e);
    };

    const displayValue = isFocused
      ? rawValue
      : formatForBlur(rawValue, maxDecimalDigits);

    const maxLength = maxIntegerDigits + (maxDecimalDigits > 0 ? maxDecimalDigits + 1 : 0);

    return (
      <div className="relative">
        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground">
          {currencyPrefix}
        </span>
        <Input
          ref={ref}
          type="text"
          inputMode="decimal"
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          maxLength={maxLength}
          className={cn("pl-14", className)}
          {...props}
        />
      </div>
    );
  }
);

MoneyInput.displayName = "MoneyInput";

export { MoneyInput };
